import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getVendors,
  createVendor,
  updateVendor,
  deleteVendor as deleteVendorApi,
} from "../../services/api/boq/VendorService";
import { Eye, Pencil, Trash2, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  gst_number: "",
  vendor_code: "",
  pancard: "",
  address: "",
  status: "active",
};

export default function VendorList() {
  const navigate = useNavigate();

  const [allVendors, setAllVendors] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const perPage = 10;
  const [lastPage, setLastPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");

  const normalize = (v) => ({
    ...v,
    status: v.status === "active" || v.status === 1 ? 1 : 0,
  });

  // ================= Vendor Code =================
  const generateVendorCode = (name, gst, pan) => {
    const clean = (str) =>
      (str || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    const namePart = clean(name).slice(0, 3) || "XXX";
    const gstPart = clean(gst).slice(0, 3) || "000";
    const panPart = clean(pan).slice(0, 3) || "XXX";

    return `${namePart}-${gstPart}-${panPart}`;
  };

  useEffect(() => {
    if (!editId) {
      const code = generateVendorCode(form.name, form.gst_number, form.pancard);
      setForm((prev) => ({ ...prev, vendor_code: code }));
    }
  }, [form.name, form.gst_number, form.pancard, editId]);

  // ================= Fetch =================
  const fetchAll = async () => {
    setLoading(true);
    try {
      let all = [];
      let pageNum = 1;
      let last = 1;

      do {
        const res = await getVendors(pageNum);
        const data = res?.data?.data;
        const list = (data?.data || []).map(normalize);

        all = [...all, ...list];
        last = data?.last_page;
        pageNum++;
      } while (pageNum <= last);

      setAllVendors(all);
      setFiltered(all);
      setLastPage(Math.ceil(all.length / perPage));
    } catch {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ================= Search =================
  useEffect(() => {
    let f = allVendors;

    // 🔍 Search filter
    if (search) {
      f = f.filter((v) =>
        [v.name, v.email, v.mobile]
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase()),
      );
    }

    // 🎯 Status filter
    if (statusFilter !== "all") {
      f = f.filter((v) =>
        statusFilter === "active" ? v.status === 1 : v.status === 0,
      );
    }

    setFiltered(f);
    setPage(1);
    setLastPage(Math.ceil(f.length / perPage));
  }, [search, statusFilter, allVendors]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // ================= Validation =================
  const validate = () => {
    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name required";

    if (!form.email && !form.mobile) {
      newErrors.email = "Email or Mobile required";
      newErrors.mobile = "Email or Mobile required";
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Invalid email";
    }

    if (form.mobile && !/^[0-9]{10}$/.test(form.mobile)) {
      newErrors.mobile = "10 digit mobile";
    }

    if (form.gst_number && !/^[0-9A-Z]{15}$/.test(form.gst_number)) {
      newErrors.gst_number = "Invalid GST";
    }

    if (form.pancard && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pancard)) {
      newErrors.pancard = "Invalid PAN";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ================= Submit =================
  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editId) {
        await updateVendor(editId, form);
        toast.success("Vendor updated");
      } else {
        await createVendor(form);
        toast.success("Vendor created");
      }

      setModalOpen(false);
      setForm(initialForm);
      fetchAll();
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // ================= Delete =================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    await deleteVendorApi(id);
    toast.success("Deleted");
    fetchAll();
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">
        <h2 className="text-2xl font-semibold">Vendors</h2>

        <div className="flex gap-2">
          <input
            className="border rounded px-3 py-2"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* STATUS FILTER */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              setForm(initialForm);
              setEditId(null);
              setErrors({});
              setModalOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded flex gap-2"
          >
            <Plus size={16} /> Add
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow border overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center">No vendors</div>
        ) : (
          <>
            {/* DESKTOP */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3 text-left">Vendor Code</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3">Mobile</th>
                    <th className="p-3">Address</th>
                    <th className="p-3">GST</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((v, i) => (
                    <tr key={v.id} className="border-t hover:bg-gray-50">
                      <td className="p-3">{i + 1}</td>
                      <td className="p-3">{v.vendor_code || "-"}</td>
                      <td className="p-3 font-medium">{v.name}</td>
                      <td className="p-3">{v.email || "-"}</td>
                      <td className="p-3">{v.mobile || "-"}</td>
                      <td
                        className="p-3 max-w-[200px] truncate cursor-help"
                        title={v.address || "No address provided"}
                      >
                        {v.address || "-"}
                      </td>
                      <td className="p-3">{v.gst_number || "-"}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            v.status
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {v.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="p-3 flex gap-3 justify-center">
                        <Eye
                          size={16}
                          className=" text-blue-600 cursor-pointer"
                          onClick={() => navigate(`/vendors/view/${v.id}`)}
                        />
                        <Pencil
                          size={16}
                          className="text-green-600 cursor-pointer"
                          onClick={() => {
                            setForm({
                              ...v,
                              status: v.status ? "active" : "inactive",
                            });
                            setEditId(v.id);
                            setModalOpen(true);
                          }}
                        />
                        <Trash2
                          size={16}
                          className="text-red-600 cursor-pointer"
                          onClick={() => handleDelete(v.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILE */}
            <div className="md:hidden p-3 space-y-3">
              {paginated.map((v) => (
                <div key={v.id} className="border p-4 rounded-xl shadow-sm">
                  <h3 className="font-semibold">{v.name}</h3>
                  <p>{v.vendor_code || "-"}</p>
                  <p>{v.email}</p>
                  <p>{v.gst_number || "-"}</p>
                  <p>{v.pancard || "-"}</p>
                  <p>{v.mobile || "-"}</p>

                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => navigate(`/vendors/view/${v.id}`)}
                      className="flex-1 bg-blue-100 text-blue-600 rounded py-1"
                    >
                      View
                    </button>
                    <button
                      onClick={() => {
                        setForm({
                          ...v,
                          status: v.status ? "active" : "inactive",
                        });
                        setEditId(v.id);
                        setModalOpen(true);
                      }}
                      className="flex-1 bg-green-100 text-green-600 rounded py-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(v.id)}
                      className="flex-1 bg-red-100 text-red-600 rounded py-1"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* PAGINATION */}
      <div className="flex justify-end mt-4 gap-2">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="px-3 py-1 border rounded"
        >
          Prev
        </button>
        <span>
          {page}/{lastPage}
        </span>
        <button
          disabled={page === lastPage}
          onClick={() => setPage(page + 1)}
          className="px-3 py-1 border rounded"
        >
          Next
        </button>
      </div>

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white w-full max-w-lg rounded-xl">
            <div className="flex justify-between p-4 border-b">
              <h3 className="font-semibold">
                {editId ? "Edit Vendor" : "Create Vendor"}
              </h3>
              <X onClick={() => setModalOpen(false)} />
            </div>

            <form onSubmit={submit} className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="name"
                  label="Name"
                  form={form}
                  setForm={setForm}
                  error={errors.name}
                />

                <Input
                  name="email"
                  label="Email"
                  form={form}
                  setForm={setForm}
                  error={errors.email}
                />

                <Input
                  name="mobile"
                  label="Mobile"
                  form={form}
                  setForm={setForm}
                  error={errors.mobile}
                />

                <Input
                  name="gst_number"
                  label="GST No"
                  form={form}
                  setForm={setForm}
                  error={errors.gst_number}
                />

                <Input
                  name="pancard"
                  label="PAN Card"
                  form={form}
                  setForm={setForm}
                  error={errors.pancard}
                />

                <Input
                  name="vendor_code"
                  label="Vendor Code"
                  form={form}
                  readOnly
                />

                {/* Full width field */}
                <div className="md:col-span-2">
                  <Input
                    name="address"
                    label="Address"
                    form={form}
                    setForm={setForm}
                  />
                </div>

                {/* Status full width */}
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-3 py-2 mt-1"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 mt-4 border-t pt-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  {saving
                    ? "Saving..."
                    : editId
                      ? "Update Vendor"
                      : "Save Vendor"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const Input = ({ name, label, form, setForm, error, readOnly }) => {
  return (
    <div>
      <label className="text-sm">{label}</label>
      <input
        value={form[name] || ""}
        readOnly={readOnly}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            [name]:
              name === "mobile"
                ? e.target.value.replace(/\D/g, "").slice(0, 10)
                : name === "gst_number" || name === "pancard"
                  ? e.target.value.toUpperCase()
                  : e.target.value,
          }))
        }
        className={`w-full border p-2 rounded ${
          error ? "border-red-500" : ""
        } ${readOnly ? "bg-gray-100" : ""}`}
      />
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
};
